import React, { JSX, Suspense, lazy } from "react";
import "@fontsource/playfair-display";
import "@fontsource/montserrat";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import GlobalStylesComponent from "./styles/GlobalStyles";
import theme from "./styles/theme";
import { AppProvider } from "./context/AppContext";
import { getToken, isTokenExpired } from "./services/AuthService";

// Components & Pages
import Layout from "./components/Layout";
import CheckInPage from "./pages/CheckInPage";
import GuestsInfoPage from "./pages/GuestsInfoPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CheckInCompletePage from "./pages/CheckInCompletePage";
import LanguageWrapper from "./components/LanguageWrapper";
import PaymentPage from "./pages/PaymentPage";
import { Toaster } from "react-hot-toast";
import GuestDetailsPageWrapper from "./pages/GuestDetailsPageWrapper";
import { SUPPORTED_LOCALES } from "./i18n";

/** Code-split: pulls scandoc-ai-components + onnxruntime only when user opens scan-doc. */
const ScanDocPage = lazy(() => import("./pages/ScanDocPage"));

/**
 * Build sets PUBLIC_URL=/oci-checkin/app, but many hosts also expose short URLs like /en.
 * If basename does not match window.location.pathname, no Route matches → blank #root.
 */
function getRouterBasename(): string | undefined {
	const baked = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
	if (typeof window === "undefined") {
		return baked || undefined;
	}
	if (!baked) return undefined;
	const path = window.location.pathname;
	if (path.startsWith(baked)) return baked;
	const segments = path.split("/").filter(Boolean);
	const first = segments[0];
	if (first && (SUPPORTED_LOCALES as readonly string[]).includes(first)) return "";
	if (segments.length === 0) return "";
	return baked;
}

const ROUTER_BASENAME = getRouterBasename();

const scanDocFallback = (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
    </Box>
);

// Protected Route Component — requires JWT from check-in (last name + confirmation)
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
    const { lng } = useParams();
    const token = getToken();

    if (!token || isTokenExpired()) {
        return <Navigate to={lng ? `/${lng}` : "/en"} replace />;
    }

    return children;
};

const LanguageRoutes: React.FC = () => {
    const { lng } = useParams();

    // Redirect to default language if not provided
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(lng ?? "")) {
        return <Navigate to="/" replace />;
    }

    return (
        <LanguageWrapper lng={lng!}>
            <Routes>
                <Route path="/" element={<CheckInPage />} />
                <Route element={<Layout />}>
                    <Route
                        path="guest-info"
                        element={<ProtectedRoute><GuestsInfoPage /></ProtectedRoute>}
                    />
                    <Route
                        path="guest-details/:guestId"
                        element={<ProtectedRoute><GuestDetailsPageWrapper /></ProtectedRoute>}
                    />
                    <Route
                        path="scan-doc/:guestId"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={scanDocFallback}>
                                    <ScanDocPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                        handle={{ layout: { hideFooter: true, hideNavbar: false } }}
                    />
                    <Route
                        path="payment"
                        element={<ProtectedRoute><PaymentPage /></ProtectedRoute>}
                    />
                    <Route
                        path="check-in-complete"
                        element={<ProtectedRoute><CheckInCompletePage /></ProtectedRoute>}
                    />
                </Route>
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="*" element={<div>404 - Page Not Found</div>} />
            </Routes>
        </LanguageWrapper>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider theme={theme}>
            <AppProvider>
                <CssBaseline />
                <GlobalStylesComponent />
                {/* Toast notifications */}
                <Toaster position="top-center" reverseOrder={false} />
                <Router basename={ROUTER_BASENAME}>
                    <Routes>
                        {/* Protect all language-specific routes */}
                        <Route path="/:lng/*" element={<LanguageRoutes />} />
                        {/* Redirect root to default language */}
                        <Route path="*" element={<Navigate to="/en" replace />} />
                    </Routes>
                </Router>
            </AppProvider>
        </ThemeProvider>
    );
};

export default App;
